import {
  ReactNode,
  TransitionEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import styled, { css } from 'styled-components'
import { AnimationType, useAnimationState } from './useAnimationState'
import { TransitionStage } from './TransitionList/constants'
import { useNumberTransition } from 'hooks/useNumberTransition'

export interface AnimationWrapperProps {
  children: ReactNode
  duration: number
  delay: number
}

const pageAnimationStyleJS: Record<string, { translateX: number }> = {
  backInit: { translateX: -25 },
  backLeave: { translateX: 100 },
  backEnter: { translateX: 0 },
  nextInit: { translateX: 100 },
  nextEnter: { translateX: 0 },
  nextLeave: { translateX: -25 },
  overlayInit: { translateX: 100 },
  overlayEnter: { translateX: 0 },
  overlayLeave: { translateX: 0 },
}

const getActiveStyle = (type: AnimationType, stage: TransitionStage) => {
  if (type === AnimationType.back) {
    if (stage === TransitionStage.init || stage === TransitionStage.preInit)
      return pageAnimationStyleJS.backInit
    if (stage === TransitionStage.enter) return pageAnimationStyleJS.backEnter
    if (stage === TransitionStage.leave) return pageAnimationStyleJS.backLeave
  }
  if (type === AnimationType.next) {
    if (stage === TransitionStage.init || stage === TransitionStage.preInit)
      return pageAnimationStyleJS.nextInit
    if (stage === TransitionStage.enter) return pageAnimationStyleJS.nextEnter
    if (stage === TransitionStage.leave) return pageAnimationStyleJS.nextLeave
  }
  if (type === AnimationType.overlay) {
    if (stage === TransitionStage.init || stage === TransitionStage.preInit)
      return pageAnimationStyleJS.overlayInit
    if (stage === TransitionStage.enter) return pageAnimationStyleJS.overlayEnter
    if (stage === TransitionStage.leave) return pageAnimationStyleJS.overlayLeave
  }
  return undefined
}

const AnimationWrapper = ({
  children,
  duration,
  delay,
}: AnimationWrapperProps) => {
  const {
    stage,
    type,
    createAnimation,
    blockLeave,
    blockEnter,
  } = useAnimationState()

  const transitionFinishCallbacks = useRef<(() => void)[]>([])

  useEffect(() => {
    if (stage === TransitionStage.enter || stage === TransitionStage.leave) {
      transitionFinishCallbacks.current.push(createAnimation())
    }
  }, [createAnimation, stage])

  const onTransitionFinish = useCallback(
    (ev: TransitionEvent<HTMLDivElement>) => {
      if (!ev.currentTarget || ev.target !== ev.currentTarget) return
      requestAnimationFrame(() => {
        transitionFinishCallbacks.current.forEach((i) => i())
        transitionFinishCallbacks.current = []
      })
    },
    [],
  )

  useEffect(() => {
    if (stage === TransitionStage.preLeave) {
      const cb = blockLeave()
      requestAnimationFrame(() => {
        cb()
      })
    }
  }, [blockLeave, stage])

  useEffect(() => {
    if (stage === TransitionStage.preInit) {
      const cb = blockEnter()
      requestAnimationFrame(() => {
        cb()
      })
    }
  }, [blockEnter, stage])

  const activeStyles = getActiveStyle(type, stage)
  const currentXOffset = activeStyles?.translateX ?? 0

  const handleNumberTransitionEnd = useCallback(() => {
    // For fade animations, translateX never changes (always 0),
    // so useNumberTransition fires completion from mount time, not enter time.
    // Fade relies on CSS transitionEnd for opacity instead.
    if (type === AnimationType.fade) return
    requestAnimationFrame(() => {
      transitionFinishCallbacks.current.forEach((i) => i())
      transitionFinishCallbacks.current = []
    })
  }, [type])

  const smoothedXOffset = useNumberTransition(
    currentXOffset,
    duration / 1000,
    handleNumberTransitionEnd,
    delay / 1000,
  )

  return (
    <Wrapper
      onTransitionEnd={onTransitionFinish}
      $duration={duration}
      $delay={delay}
      $type={type}
      $stage={stage}
      style={{ transform: `translateX(${smoothedXOffset}%)` }}
    >
      {children}
    </Wrapper>
  )
}

interface WrapperProps {
  $duration: number
  $delay: number
  $type: AnimationType
  $stage: TransitionStage
}

const Wrapper = styled.div<WrapperProps>`
  background: var(--app-background, #121317);
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  overflow: ${(p) => p.$stage === TransitionStage.normal ? 'auto' : 'hidden'};
  overscroll-behavior: contain;
  position: fixed;
  right: 0;
  top: 0;
  transition: opacity ${(p) => p.$duration / 1000}s linear ${(p) => p.$delay / 1000}s;
  z-index: 1;

  ${(p) =>
    p.$stage === TransitionStage.preInit &&
    p.$type !== AnimationType.none &&
    css`
      visibility: hidden;
      z-index: -1;
    `}

  ${(p) =>
    p.$stage === TransitionStage.preLeave &&
    css`
      pointer-events: none;
    `}

  ${(p) =>
    p.$type === AnimationType.back &&
    (p.$stage === TransitionStage.init ||
      p.$stage === TransitionStage.preInit) &&
    css`
      z-index: 0;
    `}

  ${(p) =>
    p.$type === AnimationType.back &&
    p.$stage === TransitionStage.leave &&
    css`
      pointer-events: none;
      z-index: 2;
    `}

  ${(p) =>
    p.$type === AnimationType.back &&
    p.$stage === TransitionStage.enter &&
    css`
      z-index: 0;
    `}

  ${(p) =>
    p.$type === AnimationType.next &&
    (p.$stage === TransitionStage.init ||
      p.$stage === TransitionStage.preInit) &&
    css`
      z-index: 2;
    `}

  ${(p) =>
    p.$type === AnimationType.next &&
    p.$stage === TransitionStage.enter &&
    css`
      z-index: 2;
    `}

  ${(p) =>
    p.$type === AnimationType.next &&
    p.$stage === TransitionStage.leave &&
    css`
      pointer-events: none;
      z-index: 0;
    `}

  ${(p) =>
    p.$type === AnimationType.overlay &&
    (p.$stage === TransitionStage.init ||
      p.$stage === TransitionStage.preInit) &&
    css`
      z-index: 2;
    `}

  ${(p) =>
    p.$type === AnimationType.overlay &&
    p.$stage === TransitionStage.enter &&
    css`
      z-index: 2;
    `}

  ${(p) =>
    p.$type === AnimationType.overlay &&
    p.$stage === TransitionStage.leave &&
    css`
      pointer-events: none;
      z-index: 0;
    `}

  ${(p) =>
    p.$type === AnimationType.fade &&
    (p.$stage === TransitionStage.init ||
      p.$stage === TransitionStage.preInit) &&
    css`
      opacity: 0;
      z-index: 2;
    `}

  ${(p) =>
    p.$type === AnimationType.fade &&
    p.$stage === TransitionStage.enter &&
    css`
      opacity: 1;
      z-index: 2;
    `}

  ${(p) =>
    p.$type === AnimationType.fade &&
    p.$stage === TransitionStage.leave &&
    css`
      opacity: 0;
      pointer-events: none;
      z-index: 0;
    `}
`

export default AnimationWrapper
